<template>
  <v-dialog
    v-model="isOpen"
    max-width="600"
    persistent
  >
    <v-card>
      <v-card-title class="headline">
        {{ title }}
      </v-card-title>

      <v-card-text>
        <div class="d-flex align-center mb-4">
          <v-progress-circular
            v-if="isLoadingShow"
            :size="20"
            color="primary"
            indeterminate
            class="mr-3"
          />
          <span v-if="isLoadingShow">
            Вы можете закрыть окно и найти результат формирования отчета в уведомлениях
          </span>
        </div>

        <div v-if="isReportFailed" class="error--text" v-html="report.error_msg" />

        <v-alert
          v-if="isReportDone"
          type="success"
          text
          class="mb-0"
        >
          Отчёт готов к загрузке
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          :disabled="isDownloading"
          @click="close"
        >
          Закрыть
        </v-btn>
        <v-btn
          v-if="isReportDone"
          color="primary"
          depressed
          :loading="isDownloading"
          @click="onDownloadReport"
        >
          Загрузить отчёт
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { downloadLinkByGet } from '@/helpers/downloadBlob'
import { UsersApi } from '@/api/resources/UsersApi'
import { ReportStatuses } from '@/typings/api/Report'
import { ReportsApi } from '@/api/resources'

const STATUS_TIMEOUT = 5000

// Простой API для работы с отчетами через /reports endpoint
export default {
  name: 'UsersExportActivityModal',
  data: () => ({
    isOpen: false,
    isLoadingData: false,
    isDownloading: false,
    report: null,
    timerId: null,
    promiseResolve: null,
    promiseReject: null,
  }),
  computed: {
    title() {
      if (!this.report) {
        return 'Создание отчёта активности пользователей'
      }

      switch (this.report.status) {
        case ReportStatuses.ready:
        case ReportStatuses.pending:
        case ReportStatuses.in_progress:
          return `Отчёт #${this.report.id} формируется`
        case ReportStatuses.done:
          return `Отчёт #${this.report.id} готов к загрузке`
        case ReportStatuses.error:
          return `Не удалось сформировать отчёт #${this.report.id}`
        default:
          return this.report.status
      }
    },
    isLoadingShow() {
      return this.isLoadingData || ![ReportStatuses.done, ReportStatuses.error].includes(this.report?.status)
    },
    isReportDone() {
      return this.report && this.report.status === ReportStatuses.done
    },
    isReportFailed() {
      return this.report
        && (this.report.status === ReportStatuses.error || (this.report.error_msg
          && ![ReportStatuses.in_progress, ReportStatuses.pending].includes(this.report?.status)))
    },
  },
  beforeDestroy() {
    clearTimeout(this.timerId)
  },
  methods: {
    open() {
      this.isOpen = true
      this.report = null
      this.getOrCreateReport()
      return new Promise((resolve, reject) => {
        this.promiseResolve = resolve
        this.promiseReject = reject
      })
    },
    close(result = false) {
      clearTimeout(this.timerId)
      this.isOpen = false
      if (this.promiseResolve) {
        this.promiseResolve(result)
      }
    },
    async getOrCreateReport() {
      try {
        this.isLoadingData = true

        const usersApi = new UsersApi()
        const { data } = await usersApi.generateActivityReport()

        if (data.id) {
          this.checkStatus(data.id)
        }
      } catch (e) {
        this.$notice.errors(e)
        this.close(false)
      } finally {
        this.isLoadingData = false
      }
    },
    async checkStatus(id) {
      try {
        this.isLoadingData = true

        const reportsApi = new ReportsApi()
        const { data } = await reportsApi.getOne(id)
        this.report = data

        if (data.status !== ReportStatuses.done && data.status !== ReportStatuses.error) {
          this.timerId = setTimeout(() => {
            this.checkStatus(id)
          }, STATUS_TIMEOUT)
        } else {
          clearTimeout(this.timerId)
        }
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        this.isLoadingData = false
      }
    },
    async onDownloadReport() {
      this.isDownloading = true

      try {
        const reportsApi = new ReportsApi()
        const downloadUrl = reportsApi.getDownloadUrl(this.report.id)

        await downloadLinkByGet(downloadUrl)
        this.close(true)
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        this.isDownloading = false
      }
    },
  },
}
</script>
